<template>
  <section class="analytics-area">
    <h1>
      {{ $t('analytics.header') }}
    </h1>
    <div class="search-area">
      <div class="search-input">
        <Search />
        <input v-model="filter.search" type="text" сlass="input" :placeholder="$t('common.search') + '...'">
      </div>
    </div>
    <div class="charts">
      <div class="chart">
        <div class="header">
          <p>
            BNB {{ $t('analytics.price') }}: ${{ bnbPrice }}
          </p>
          <p>
            {{ $t('analytics.transactions') }} (24{{ $t('common.h') }}):
            <span v-if="!countLoading">
              {{ $getBigNumber(countTransactions) }}
            </span>
            <span v-else>
              <Skeleton width="30px" height="14px" />
            </span>
          </p>
        </div>
        <div class="chart-box">
          <div class="header">
            <p>
              {{ $t('analytics.liquidity') }}
            </p>
            <p>
              $0 +0%
            </p>
          </div>
          <div class="chart-container">
            <LineChart :chart-data="lineChart.data" :options="lineChart.options" />
          </div>
        </div>
      </div>
      <div class="chart">
        <div class="header">
          <p>
            BNB {{ $t('analytics.price') }}: ${{ bnbPrice }}
          </p>
          <p>
            {{ $t('analytics.transactions') }} (24{{ $t('common.h') }}):
            <span v-if="!countLoading">
              {{ $getBigNumber(countTransactions) }}
            </span>
            <span v-else>
              <Skeleton width="30px" height="14px" />
            </span>
          </p>
        </div>
        <div class="chart-box">
          <div class="header">
            <p>
              {{ $t('analytics.liquidity') }}
            </p>
            <p>
              $0 +0%
            </p>
          </div>
          <div class="chart-container">
            <BarChart :chart-data="barChart.data" :options="barChart.options" />
          </div>
        </div>
      </div>
    </div>
    <div class="top-table tokens">
      <div class="header">
        <div class="h2">
          {{ $t('analytics.top_tokens') }}
        </div>
        <div class="right">
          <nuxt-link :to="localePath('/analytics/tokens')" class="mini-button">
            {{ $t('analytics.see_all') }}
          </nuxt-link>
        </div>
      </div>
      <TopTokens />
    </div>
    <hr>
    <div class="top-table pairs">
      <div class="header">
        <div class="h2">
          {{ $t('analytics.top_pairs') }}
        </div>
        <div class="right">
          <nuxt-link :to="localePath('/analytics/pairs')" class="mini-button">
            {{ $t('analytics.see_all') }}
          </nuxt-link>
        </div>
      </div>
      <TopPairs />
    </div>
    <hr>
    <div class="top-table transactions">
      <div class="header">
        <div class="h2">
          {{ $t('analytics.transactions') }}
        </div>
        <div class="right">
          <nuxt-link :to="localePath('/analytics/transactions')" class="mini-button">
            {{ $t('analytics.see_all') }}
          </nuxt-link>
        </div>
      </div>
      <TopTransactions />
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'
import { Skeleton } from 'vue-loading-skeleton'

export default {
  name: 'AnalyticsPage',
  components: {
    BarChart: hydrateWhenIdle(() => import('~/components/analytics/parts/BarChart')),
    LineChart: hydrateWhenIdle(() => import('~/components/analytics/parts/LineChart')),
    Search: hydrateWhenVisible(() => import('~/components/svg/new/Search')),
    Skeleton,
    TopPairs: hydrateWhenIdle(() => import('~/components/analytics/tables/TopPairs')),
    TopTokens: hydrateWhenIdle(() => import('~/components/analytics/tables/TopTokens')),
    TopTransactions: hydrateWhenIdle(() => import('~/components/analytics/tables/TopTransactions'))
  },
  layout: 'new',
  data() {
    const chartColor = !this.darkTheme ? 'rgb(60, 222, 235)' : 'rgb(255, 60, 159)'
    return {
      bnbPrice: 0,
      bnbInterval: null,
      bnbIntervalTimeout: 15000,
      filter: {
        search: ''
      },
      chartsFilter: {
        period: 1
      },
      lineChart: {
        data: {
          datasets: [
            {
              borderWidth: 2,
              radius: 0,
              fill: false,
              data: [],
              borderColor: chartColor
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: 1.5,
          interaction: {
            intersect: false
          },
          plugins: {
            legend: false
          },
          scales: {
            x: {
              type: 'linear'
            }
          }
        }
      },
      barChart: {
        data: {
          datasets: [
            {
              borderWidth: 0,
              radius: 10,
              data: [],
              borderColor: chartColor,
              backgroundColor: chartColor,
              color: chartColor
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          aspectRatio: 1.5,
          interaction: {
            intersect: false
          },
          plugins: {
            legend: false
          },
          scales: {
            x: {
              type: 'var'
            }
          }
        }
      }
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head() {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : this.$config.appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['darkTheme']),
    ...mapGetters('transactions', [
      'countTransactions',
      'countLoading'
    ]),
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    }
  },
  beforeMount() {
    const lineChartData = []
    const barChartData = []
    const prev = 100
    const prev2 = 100
    for (let i = 0; i < 50; i++) {
      lineChartData.push(prev + Math.round(Math.random() * 70 - Math.random() * 50))
    }
    for (let i = 0; i < 50; i++) {
      barChartData.push(prev2 + Math.round(Math.random() * 70 - Math.random() * 50))
    }
    this.lineChart.data.labels = lineChartData
    this.lineChart.data.datasets[0].data = lineChartData
    this.barChart.data.labels = barChartData
    this.barChart.data.datasets[0].data = barChartData
  },
  mounted() {
    this.fetchTransactionsCount({
      days: 1
    })
    this.refreshBnbPrice()
    this.bnbInterval = setInterval(() => {
      this.refreshBnbPrice()
    }, this.bnbIntervalTimeout)
  },
  methods: {
    ...mapActions('transactions', [
      'fetchTransactionsCount'
    ]),
    async refreshBnbPrice() {
      const endpoint = 'https://api.coingecko.com/api/v3/simple/price'
      const newPrice = await this.$axios.$get(endpoint, {
        params: {
          vs_currencies: 'usd',
          ids: 'binancecoin'
        }
      })
      this.bnbPrice = newPrice.binancecoin.usd
    }
  }
}
</script>

<style scoped>
    @import url('~/assets/css/new/analytics.css');
</style>
